import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useAppThunkDispatch } from 'state/store';
import type { BasicInfoForm } from 'types/store/learningTeam';
import { getLDUsers } from 'state/UsersManagement/usersManagementSlice';
import {
  selectNonTeamMembers,
  selectTeamManagers,
  updateLearningTeam,
  selectIsUpdatingLearningTeam,
  selectLearningTeam,
  fetchEligibleParentsForTeam,
  selectEligibleParentsForDropdown,
  resetEligibleParents,
} from 'state/LearningTeam/learningTeamSlice';
import { serializeUsersForDropdown } from '../../utils';
import { PATHS, SETTINGS_SECTIONS, SETTINGS_TABS } from 'utils/constants';

export const useBasicInfoForm = () => {
  const dispatch = useAppThunkDispatch();
  const history = useHistory();
  const team = useSelector(selectLearningTeam);
  const eligibleParents = useSelector(selectEligibleParentsForDropdown);
  const nonTeamMembers = useSelector(selectNonTeamMembers);
  const teamManagers = useSelector(selectTeamManagers);
  const isUpdatingLearningTeam = useSelector(selectIsUpdatingLearningTeam);
  const [basicInfo, setBasicInfo] = useState<BasicInfoForm | undefined>(
    undefined
  );

  // Sync form state when the loaded team changes (e.g. navigation or post-save update)
  useEffect(() => {
    if (!team) return;
    setBasicInfo({
      name: team.name ?? '',
      description: team.description ?? '',
      parent_id: team.parent_id ?? '',
      teamManagers: team.managersIds ?? [],
    });
  }, [team]);

  useEffect(() => {
    if (!team?.id) return;
    dispatch(fetchEligibleParentsForTeam(team.id));
    return () => {
      dispatch(resetEligibleParents());
    };
  }, [dispatch, team?.id]);

  useEffect(() => {
    dispatch(getLDUsers());
  }, [dispatch]);

  const handleChangeField = useCallback(
    (field: keyof BasicInfoForm, value: string | string[]) => {
      setBasicInfo((prevState) => {
        if (!prevState) return prevState;
        return { ...prevState, [field]: value };
      });
    },
    []
  );

  const selectedParentTeam = useMemo(() => {
    return eligibleParents.find(
      (learningTeam) => learningTeam.value === team?.parent_id
    );
  }, [eligibleParents, team?.parent_id]);

  const teamManagersOptions = useMemo(() => {
    return serializeUsersForDropdown(nonTeamMembers);
  }, [nonTeamMembers]);

  const selectedTeamManagers = useMemo(() => {
    return teamManagers ? serializeUsersForDropdown(teamManagers) : [];
  }, [teamManagers]);

  // Validate required fields are not empty
  const isValid = useMemo(() => {
    const name = basicInfo?.name?.trim() ?? '';
    return name.length > 0;
  }, [basicInfo?.name]);

  // Build updateFields with only the basic info fields that actually changed
  const changedUpdateFields = useMemo((): Partial<BasicInfoForm> => {
    if (!basicInfo || !team) return {};

    const updateFields: Partial<BasicInfoForm> = {};

    if (basicInfo.name?.trim() !== team.name?.trim()) {
      updateFields.name = basicInfo.name?.trim() ?? '';
    }
    if (basicInfo.description?.trim() !== (team.description?.trim() ?? '')) {
      updateFields.description = basicInfo.description?.trim() ?? '';
    }
    if ((basicInfo.parent_id || '') !== (team.parent_id || '')) {
      updateFields.parent_id = basicInfo.parent_id || '';
    }

    const originalManagers = team.managersIds || [];
    const currentManagers = basicInfo.teamManagers || [];
    const managersChanged =
      originalManagers.length !== currentManagers.length ||
      !originalManagers.every((id) => currentManagers.includes(id));
    if (managersChanged) {
      updateFields.teamManagers = basicInfo.teamManagers ?? [];
    }

    return updateFields;
  }, [basicInfo, team]);

  // Derived from changedUpdateFields: true when any basic info field changed
  const hasChanges = Object.keys(changedUpdateFields).length > 0;

  const canSave = isValid && hasChanges;

  const handleUpdate = useCallback(async () => {
    if (!team?.id || !canSave) return;
    await dispatch(
      updateLearningTeam({
        id: team.id,
        updateFields: changedUpdateFields,
      })
    );
  }, [dispatch, canSave, team?.id, changedUpdateFields]);

  const handleCancel = useCallback(() => {
    history.push(
      `${PATHS.SETTINGS}/${SETTINGS_TABS.TEAMS}?section=${SETTINGS_SECTIONS.LD_TEAM}`
    );
  }, [history]);

  return useMemo(
    () => ({
      basicInfo,
      handleChangeField,
      learningTeamsOptions: eligibleParents,
      selectedParentTeam,
      teamManagersOptions,
      selectedTeamManagers,
      canSave,
      isValid,
      hasChanges,
      handleUpdate,
      handleCancel,
      isUpdatingLearningTeam,
    }),
    [
      basicInfo,
      handleChangeField,
      eligibleParents,
      selectedParentTeam,
      teamManagersOptions,
      selectedTeamManagers,
      canSave,
      isValid,
      hasChanges,
      handleUpdate,
      handleCancel,
      isUpdatingLearningTeam,
    ]
  );
};
